import React, { useCallback, useState } from 'react';
import Pagination from '@react/widget/Pagination';
import { type Locale } from '@i18next/ui';
import clsx from "clsx";
import Provider from '@module/apollo/client/Provider';
import useGetInTheNewsQuery from "@graphql/inTheNews/entries.CSR";

interface Props {
  siteHandle: Locale;
  limit?: number;
  page?: number;
  year?: string;
  label: Record<string, string>;
  changeTotal?: (page: number) => void;
};

const InTheNewsContent:React.FC<Props> = ({
  siteHandle,
  limit = 9,
  page:p = 1,
  year = "*",
  label
}) => {
  const [page, setPage] = useState<number>(p);
  const offset = (page - 1) * limit;
  const currentYear = new Date().getFullYear();
  const postDate = year === "*" 
    ? ["and", ">= 2014-01-01", `< ${currentYear + 1}-01-01`] 
    : ["and", `>= ${year}-01-01`, `< ${parseInt(year) + 1}-01-01`];

  const { loading, error, data } = useGetInTheNewsQuery({
    site: siteHandle,
    limit,
    offset,
    postDate,
    // refetch,
    // preview
  });

  if (loading) return <div className="mt-12 pt-12 max-w-4xl mx-auto"><p>{label?.load}...</p></div>
  if (error) return <div className="mt-12 pt-12 max-w-4xl mx-auto"><p>Error: {error.message}</p></div>;
  if (!data || data?.entryCount <= 0) return <div className="mt-12 pt-12 max-w-4xl mx-auto"><p>{label?.nodata}</p></div>;

  const total = Math.ceil(data.entryCount / limit);

  return (
    <>
      <ul className="max-w-4xl mx-auto border-t [&>li:not(:last-child)]:border-b">
        {data.entries.map(({ title, insertLink: { url } }) => (
          <li key={title}>
            <a
              href={(!!url && url !== "#") ? url : undefined}
              target="_blank"
              className={clsx(
                "btn flex gap-6 justify-between items-center transition duration-300 w-full py-6 rounded-full group/btn",
                url !== "#" ? "hover:text-primary-hover cursor-pointer" : ""
              )}
            >
              <h3 className="text-lg">{title}</h3>
              {url && url !== "#" && (
                <div className="btn-icon group-hover/btn:translate-x-2">
                  <svg
                    className="icon w-4 h-4 text-red-500"
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      d="M9.893 2.036h4.071m0 4.07v-4.07m-6 6.003 6-6.003m-6 .004h-4.5a1.5 1.5 0 0 0-1.5 1.5v9a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5v-4.5"
                    />
                  </svg>
                </div>
              )}
            </a>
          </li>
        ))}
      </ul>
      <Pagination
        label={{
          prev: label?.prev || "Prev",
          next: label?.next || "Next"
        }}
        page={page}
        total={total}
        onChange={(e) => setPage(e)}
      />
    </>
  );
};

const InTheNews: React.FC<Props> = ({ siteHandle, label }) => {
  const [year, setYear] = useState<string>("*");
  const [page, setPage] = useState<number>(1);
  const years = Array.from({ length: new Date().getFullYear() - 2014 + 1 }, (_, i) => `${2014 + i}`).reverse();

  const handleYearChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    setPage(1);
  }, []);
  //console.log("InTheNewsCount", ++InTheNewsCount);
  return (
    <>
      <div className="max-w-4xl mx-auto mb-8">
        <label className="block mb-2 text-lg font-semibold">{label?.yearLable}</label>
        <select
          className="block w-full rounded-md border-gray-100 focus:border-gray-300"
          defaultValue={year}
          onChange={handleYearChange}
        >
          <option value="*">{label?.allLable}</option>
          {years.map((_year) => (
            <option key={_year} value={_year}>
              {_year}
            </option>
          ))}
        </select>
      </div>
      <Provider>
        <InTheNewsContent
          siteHandle={siteHandle}
          limit={9}
          page={page}
          year={year}
          label={label}
        />
      </Provider>
    </>
  );
};

export default InTheNews;